<template>
  <div class="flex flex-col gap-5">
    <!-- The groups first: real visits, not a box of filters (spec §7.4) -->
    <!-- the pills hold a name and a count, wider than a plain choice -->
    <Picker mode="pills" class="[&>button]:max-w-none" :model-value="group" :options="groupOptions" :disabled="disabled" :label="t('mail_segment_group_label')"
      @update:model-value="(k: unknown) => setGroup(k as GroupKey)" />

    <!-- "Last year, not yet this year" asks its two activities right here -->
    <div v-if="group === 'lapsed'" class="grid gap-4 sm:grid-cols-2">
      <Field :label="t('mail_segment_events')" v-slot="{ id }">
        <Picker :id="id" v-model="eventIds" multiple mode="field" :options="eventOptions" :disabled="disabled" :placeholder="t('mail_segment_lapsed_came_pick')" />
      </Field>
      <Field :label="t('mail_segment_exclude_events')" v-slot="{ id }">
        <Picker :id="id" v-model="excludeEventIds" multiple mode="field" :options="eventOptions" :disabled="disabled" :placeholder="t('mail_segment_lapsed_not_pick')" />
      </Field>
    </div>

    <div class="flex flex-wrap items-baseline gap-x-3 gap-y-1" aria-live="polite">
      <ProgressSpinner v-if="counting && !count" class="!size-6" strokeWidth="6" />
      <template v-else-if="count">
        <span class="text-4xl font-extrabold tracking-tight tabular-nums text-ink dark:text-surface-0">{{ num(count.reachable) }}</span>
        <span class="text-surface-600 dark:text-surface-300">
          <template v-if="count.total === 0">{{ t('mail_segment_count_nobody') }}</template>
          <template v-else-if="narrowed && groupCounts[group] != null">{{ t('mail_segment_count_of_group', { n: num(groupCounts[group]!) }) }}</template>
          <template v-else>{{ t('mail_segment_count_people', count.reachable) }}</template>
          <template v-if="count.total > count.reachable"> · {{ t('mail_segment_count_skipped', { n: num(count.total - count.reachable) }) }}</template>
        </span>
      </template>
    </div>

    <!-- Smaller: one filter at a time, dashed while empty -->
    <div class="flex flex-col gap-3">
      <div class="flex flex-wrap items-center gap-1.5">
        <span class="text-sm text-surface-600 dark:text-surface-300 mr-1">{{ t('mail_segment_narrow_label') }}</span>
        <button v-for="f in filters" :key="f.key" type="button" :disabled="disabled" :aria-expanded="openFilter === f.key"
          :class="[pill, f.on ? pillOn : pillDashed]" @click="openFilter = openFilter === f.key ? null : f.key">
          <span class="truncate">{{ f.label }}</span>
        </button>
        <button v-if="modelValue.attended" type="button" :disabled="disabled" :class="[pill, pillOn]"
          :aria-label="t('mail_segment_filter_remove', { name: t('mail_segment_attendance_came') })"
          @click="emit('update:modelValue', { ...modelValue, attended: undefined })">
          {{ t('mail_segment_attendance_came') }} <AppIcon icon="xmark" />
        </button>
      </div>

      <div v-if="openFilter" class="flex flex-col gap-3 p-4 bg-[#F7F7F5] dark:bg-surface-800">
        <template v-if="openFilter === 'event'">
          <Field :label="t('mail_segment_events')" v-slot="{ id }">
            <Picker :id="id" v-model="eventIds" multiple mode="field" :options="eventOptions" :placeholder="t('mail_segment_events_any')" />
          </Field>
          <Field v-if="eventIds.length && ticketTypes.length" :label="t('mail_segment_ticket_types')" v-slot="{ id }">
            <Picker :id="id" v-model="ticketTypeIds" multiple :options="ticketTypeOptions" :placeholder="t('mail_segment_ticket_types_any')" />
          </Field>
        </template>
        <Field v-else-if="openFilter === 'period'" :label="t('mail_segment_period')" :hint="t('mail_segment_period_help')" v-slot="{ id, describedby }">
          <DatePicker :inputId="id" v-model="period" selectionMode="range" :manualInput="false" :aria-describedby="describedby"
            dateFormat="dd-mm-yy" showIcon iconDisplay="input" showButtonBar :placeholder="t('mail_segment_period_any')" class="w-full sm:w-80" />
        </Field>
        <template v-else-if="openFilter === 'tags'">
          <Field :label="t('mail_segment_tags')" v-slot="{ id }">
            <Picker :id="id" v-model="tags" multiple :options="allTags.map(tag => ({ value: tag, label: tag }))" />
          </Field>
          <Picker v-if="tags.length > 1" mode="pills" v-model="tagMode" :options="tagModes" :label="t('mail_segment_tags')" />
        </template>
        <Field v-else-if="openFilter === 'lang'" :label="t('mail_segment_languages')" v-slot="{ id }">
          <Picker :id="id" v-model="languages" multiple mode="pills" :options="languageOptions.map(l => ({ value: l.code, label: l.label }))" />
        </Field>
        <div class="flex items-center gap-4">
          <button type="button" :class="link" @click="openFilter = null">{{ t('done') }}</button>
          <button v-if="filters.find(f => f.key === openFilter)?.on" type="button" :class="link" @click="clearFilter(openFilter!)">
            {{ t('mail_segment_filter_clear') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { useI18n } from 'vue-i18n';
import type { Database } from '~~/types/supabase';
import { MAIL_LANGUAGES } from '~/composables/useMail';
import { FOCUS_RING, PILL_ON } from '~/utils/picker';
import { REGULAR_ORDERS } from '~/utils/contactActivity';

export type Segment = {
  event_ids?: string[];
  ticket_type_ids?: string[];
  visited_from?: string | null;
  visited_to?: string | null;
  tags_any?: string[];
  tags_all?: string[];
  languages?: string[];
  exclude_event_ids?: string[];
  min_orders?: number | null;
  // first paid order in the last twelve months / no paid order in them (mail_segment_contacts)
  new_this_year?: boolean;
  not_seen_year?: boolean;
  attended?: boolean;
  no_show?: boolean;
  // service messages (mail_campaigns.kind = 'service'): one activity, its timeslots, upcoming only
  timeslot_ids?: string[];
  upcoming_only?: boolean;
};
// news: contacts / with consent; service: addresses / reachable, valid tickets, orders without an address
export type SegmentCount = { total: number; reachable: number; tickets?: number; no_email?: number };

const props = defineProps<{ modelValue: Segment; disabled?: boolean }>();
const emit = defineEmits<{ 'update:modelValue': [Segment]; count: [SegmentCount | null] }>();

const { t } = useI18n();
const supabase = useSupabaseClient<Database>();
const { organizationId, languages: orgLanguages } = useMail();

const { num, date } = useLocaleFormat();

const pill = `inline-flex items-center gap-1.5 h-8 px-3 rounded-full border text-[13px] max-w-[260px] cursor-pointer disabled:cursor-not-allowed disabled:opacity-60 ${FOCUS_RING}`;
const pillOn = `font-semibold ${PILL_ON}`;
const pillDashed = 'font-medium border-dashed bg-surface-0 text-surface-600 border-[#CFCFCB] dark:bg-surface-900 dark:text-surface-300 dark:border-surface-600';
const link = 'p-0 border-0 bg-transparent text-sm font-semibold underline underline-offset-4 cursor-pointer text-ink dark:text-surface-0';

// Each field reads from and writes back into the one segment object.
const field = <K extends keyof Segment>(key: K, empty: NonNullable<Segment[K]>) => computed({
  get: () => (props.modelValue[key] ?? empty) as NonNullable<Segment[K]>,
  set: (value) => emit('update:modelValue', { ...props.modelValue, [key]: value }),
});
const eventIds = field('event_ids', []);
const ticketTypeIds = field('ticket_type_ids', []);
const languages = field('languages', []);
const excludeEventIds = field('exclude_event_ids', []);

// Groups: each replaces the group keys and keeps the filters that narrow it
type GroupKey = 'everyone' | 'regulars' | 'new_this_year' | 'not_seen_year' | 'lapsed' | 'no_shows';
const GROUPS: Record<GroupKey, Segment> = {
  everyone: {},
  regulars: { min_orders: REGULAR_ORDERS },
  new_this_year: { new_this_year: true },
  not_seen_year: { not_seen_year: true },
  lapsed: {},
  no_shows: { no_show: true },
};
const GROUP_KEYS: (keyof Segment)[] = ['min_orders', 'new_this_year', 'not_seen_year', 'no_show', 'exclude_event_ids'];
const lapsedPicking = ref(false);
const group = computed<GroupKey>(() => {
  const m = props.modelValue;
  if (m.exclude_event_ids?.length || lapsedPicking.value) return 'lapsed';
  if (m.min_orders) return 'regulars';
  if (m.new_this_year) return 'new_this_year';
  if (m.not_seen_year) return 'not_seen_year';
  if (m.no_show) return 'no_shows';
  return 'everyone';
});
const setGroup = (k: GroupKey) => {
  lapsedPicking.value = k === 'lapsed';
  const rest = Object.fromEntries(Object.entries(props.modelValue).filter(([key]) => !GROUP_KEYS.includes(key as keyof Segment)));
  emit('update:modelValue', { ...rest, ...GROUPS[k] });
};

// How many with consent each group has, for the pills (lapsed depends on its activities)
const groupCounts = ref<Partial<Record<GroupKey, number>>>({});
const loadGroupCounts = async () => {
  if (!organizationId.value) return;
  const keys = (Object.keys(GROUPS) as GroupKey[]).filter(k => k !== 'lapsed');
  const rows = await Promise.all(keys.map(k => supabase.rpc('mail_segment_count', { p_org: organizationId.value!, p_segment: GROUPS[k] as never })));
  groupCounts.value = Object.fromEntries(keys.map((k, i) => [k, (rows[i]?.data as SegmentCount[] | null)?.[0]?.reachable ?? 0]));
};
const groupOptions = computed(() => (Object.keys(GROUPS) as GroupKey[]).map(k => ({
  value: k,
  label: groupCounts.value[k] != null ? `${t(`mail_segment_group_${k}`)} · ${num(groupCounts.value[k]!)}` : t(`mail_segment_group_${k}`),
})));

const tagModes = computed(() => [
  { label: t('mail_segment_tags_mode_any'), value: 'any' },
  { label: t('mail_segment_tags_mode_all'), value: 'all' },
]);
const tagMode = computed({
  get: () => (props.modelValue.tags_all?.length ? 'all' : 'any'),
  set: (mode) => emit('update:modelValue', { ...props.modelValue, tags_any: mode === 'any' ? tags.value : [], tags_all: mode === 'all' ? tags.value : [] }),
});
const tags = computed({
  get: () => (props.modelValue.tags_all?.length ? props.modelValue.tags_all : props.modelValue.tags_any ?? []),
  set: (value: string[]) => emit('update:modelValue', {
    ...props.modelValue,
    tags_any: tagMode.value === 'any' ? value : [],
    tags_all: tagMode.value === 'all' ? value : [],
  }),
});

// Dates are plain calendar days (YYYY-MM-DD); toISOString would shift them a day in CET.
const toDay = (d: Date | null | undefined) =>
  d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}` : null;
const fromDay = (s: string | null | undefined) => (s ? new Date(`${s}T00:00:00`) : null);
const period = computed({
  get: () => (props.modelValue.visited_from || props.modelValue.visited_to
    ? [fromDay(props.modelValue.visited_from), fromDay(props.modelValue.visited_to)]
    : null) as Date[] | null,
  set: (value: (Date | null)[] | null) => emit('update:modelValue', {
    ...props.modelValue,
    visited_from: toDay(value?.[0]),
    visited_to: toDay(value?.[1] ?? value?.[0]),
  }),
})

type FilterKey = 'event' | 'period' | 'tags' | 'lang';
const openFilter = ref<FilterKey | null>(null);
// "3 activiteiten" past one name, so a pill stays one short line
const shortList = (names: string[]) => (names.length > 1 ? `${names[0]} +${names.length - 1}` : names[0] ?? '');
const filters = computed(() => {
  const list: { key: FilterKey; on: boolean; label: string }[] = [];
  // in "lapsed" the activities belong to the group itself
  if (group.value !== 'lapsed') {
    const names = eventIds.value.map(id => events.value.find(e => e.id === id)?.title ?? '');
    list.push({ key: 'event', on: !!names.length, label: names.length ? `${t('mail_segment_events')} ${shortList(names)}` : t('mail_segment_filter_event') });
  }
  const p = period.value;
  list.push({ key: 'period', on: !!p, label: p ? `${t('mail_segment_filter_period')}: ${date(p[0])} – ${date(p[1] ?? p[0])}` : t('mail_segment_filter_period') });
  if (allTags.value.length || tags.value.length) list.push({ key: 'tags', on: !!tags.value.length, label: tags.value.length ? `${t('mail_segment_filter_tag')}: ${shortList(tags.value)}` : t('mail_segment_filter_tag') });
  if (languageOptions.value.length > 1) {
    const names = languages.value.map(c => MAIL_LANGUAGES.find(l => l.code === c)?.label ?? c);
    list.push({ key: 'lang', on: !!names.length, label: names.length ? `${t('mail_segment_languages')}: ${shortList(names)}` : t('mail_segment_languages') });
  }
  return list;
});
// Narrowed past the group: the count then says "of the n in this group"
const narrowed = computed(() => filters.value.some(f => f.on) || !!props.modelValue.attended);
const clearFilter = (key: FilterKey) => {
  const m = { ...props.modelValue };
  if (key === 'event') { m.event_ids = []; m.ticket_type_ids = []; }
  if (key === 'period') { m.visited_from = null; m.visited_to = null; }
  if (key === 'tags') { m.tags_any = []; m.tags_all = []; }
  if (key === 'lang') m.languages = [];
  emit('update:modelValue', m);
  openFilter.value = null;
};

const languageOptions = computed(() => MAIL_LANGUAGES.filter(l => orgLanguages.value.includes(l.code)));

// Options
const events = ref<{ id: string; title: string }[]>([]);
const eventOptions = computed(() => events.value.map(e => ({ value: e.id, label: e.title })));
const ticketTypes = ref<{ id: string; label: string }[]>([]);
const allTags = ref<string[]>([]);

const loadOptions = async () => {
  if (!organizationId.value) return;
  const [ev, tg] = await Promise.all([
    supabase.from('events').select('id, title').eq('organization_id', organizationId.value).eq('is_template', false)
      .order('created_at', { ascending: false }),
    // ponytail: every contact's tags, deduped here; a distinct-tags RPC once lists get big
    supabase.from('mail_contacts').select('tags').eq('organization_id', organizationId.value).neq('tags', '{}' as never),
  ]);
  events.value = ev.data ?? [];
  allTags.value = [...new Set((tg.data ?? []).flatMap(r => r.tags))].sort((a, b) => a.localeCompare(b));
};
watch(organizationId, () => { loadOptions(); loadGroupCounts(); }, { immediate: true });
const ticketTypeOptions = computed(() => ticketTypes.value.map(tt => ({ value: tt.id, label: tt.label })));

watch(() => eventIds.value.join(), async () => {
  const ids = eventIds.value;
  if (!ids.length) {
    ticketTypes.value = [];
    if (ticketTypeIds.value.length) ticketTypeIds.value = [];
    return;
  }
  const { data } = await supabase.from('ticket_types').select('id, name, event_id').in('event_id', ids);
  const title = (id: string) => events.value.find(e => e.id === id)?.title ?? '';
  ticketTypes.value = (data ?? []).map(tt => ({ id: tt.id, label: ids.length > 1 ? `${title(tt.event_id)} · ${tt.name}` : tt.name }));
  // Drop ticket types of events that were deselected
  const kept = ticketTypeIds.value.filter(id => ticketTypes.value.some(tt => tt.id === id));
  if (kept.length !== ticketTypeIds.value.length) ticketTypeIds.value = kept;
}, { immediate: true });

// Live count
const count = ref<SegmentCount | null>(null);
const counting = ref(false);
const recount = async () => {
  if (!organizationId.value) return;
  counting.value = true;
  const { data, error } = await supabase.rpc('mail_segment_count', {
    p_org: organizationId.value,
    p_segment: props.modelValue as never,
  });
  counting.value = false;
  count.value = error ? null : ((data as SegmentCount[] | null)?.[0] ?? { total: 0, reachable: 0 });
  emit('count', count.value);
};
watchDebounced(() => [organizationId.value, props.modelValue], recount, { debounce: 400, deep: true, immediate: true });
</script>
